import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';

const ORIGINE = {
    fusion_agences: 'ancienne fusion d\'agences',
    commercial_change_agence: 'le commercial a changé d\'agence',
    importation: 'importation de données',
    erreur_rattachement: 'erreur de rattachement probable',
};

const CACHE = { at: 0, data: null, pending: null, uid: null, subs: new Set() };
const TTL = 5 * 60 * 1000;

function fetchAnomalies(force, uid) {
    if (CACHE.uid !== uid) Object.assign(CACHE, { uid, data: null, at: 0, pending: null });
    if (!force && CACHE.data && Date.now() - CACHE.at < TTL) return Promise.resolve(CACHE.data);
    if (!force && CACHE.pending) return CACHE.pending;
    CACHE.pending = axios.get(`${API_URL}/api/anomalies/rattachements`, { withCredentials: true })
        .then(r => { CACHE.data = r.data; CACHE.at = Date.now(); CACHE.subs.forEach(fn => fn(r.data)); return r.data; })
        .catch(() => null).finally(() => { CACHE.pending = null; });
    return CACHE.pending;
}

function useAnomalies() {
    const { user } = useAuth();
    const uid = user?.id;
    const [d, setD] = useState(CACHE.uid === uid ? CACHE.data : null);
    const load = useCallback(force => fetchAnomalies(force, uid).then(setD), [uid]);
    useEffect(() => { CACHE.subs.add(setD); load(false); return () => { CACHE.subs.delete(setD); }; }, [load]);
    return [d, () => load(true)];
}

export function RattachementAlert() {
    const [d] = useAnomalies();
    if (!d?.count) return null;
    return (
        <div className="mx-4 mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-[13px] text-amber-900 flex gap-2 items-start" data-testid="rattachement-alert">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
                <b>{d.count} vente{d.count > 1 ? 's' : ''} non comptée{d.count > 1 ? 's' : ''} en commission</b> : le commercial et le véhicule sont dans deux agences différentes ({d.items.map(i => i.immatriculation).join(', ')}).
                {d.peut_corriger ? <> <Link to="/coherence" className="underline font-medium" data-testid="rattachement-alert-link">Corriger</Link></> : ' Le super administrateur doit corriger le rattachement.'}
            </div>
        </div>
    );
}

function Row({ it, onDone }) {
    const [action, setAction] = useState('');
    const [commercialId, setCommercialId] = useState('');
    const [motif, setMotif] = useState('');
    const [busy, setBusy] = useState(false);
    const [commerciaux, setCommerciaux] = useState([]);
    useEffect(() => {
        if (action === 'changer_commercial' && !commerciaux.length)
            axios.get(`${API_URL}/api/users`, { withCredentials: true }).then(r => setCommerciaux((r.data || []).filter(u => u.role === 'user' && u.agency_id === it.vehicle_agency_id))).catch(() => {});
    }, [action, commerciaux.length, it.vehicle_agency_id]);
    const submit = async () => {
        setBusy(true);
        try {
            await axios.post(`${API_URL}/api/anomalies/rattachements/${it.vehicle_id}/corriger`, { action, commercial_id: commercialId || null, motif }, { withCredentials: true });
            toast.success('Rattachement corrigé');
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || 'Correction impossible'); }
        setBusy(false);
    };
    return (
        <li className="rounded-lg bg-white border border-amber-200 p-3 space-y-2" data-testid={`rattachement-${it.immatriculation}`}>
            <p className="break-words"><b>{it.immatriculation}</b> {it.nom} — vendu le {it.date_vente || '—'} · véhicule : <b>{it.vehicle_agency}</b> · commercial : <b>{it.commercial}</b> ({it.commercial_agency})</p>
            <p className="text-amber-800">Origine probable : {ORIGINE[it.origine] || it.origine}{it.mois_deja_paye ? ' · commission de ce mois déjà payée' : ''}</p>
            <div className="flex flex-wrap gap-2 items-center">
                <select className="border rounded-md px-2 py-1.5 bg-white w-full sm:w-auto max-w-full" value={action} onChange={e => setAction(e.target.value)} data-testid={`rattachement-action-${it.immatriculation}`}>
                    <option value="">Choisir une correction…</option>
                    <option value="deplacer_vehicule">Le véhicule appartient à {it.commercial_agency}</option>
                    <option value="changer_commercial">Vendu par un commercial de {it.vehicle_agency}</option>
                </select>
                {action === 'changer_commercial' && (
                    <select className="border rounded-md px-2 py-1.5 bg-white w-full sm:w-auto max-w-full" value={commercialId} onChange={e => setCommercialId(e.target.value)} data-testid={`rattachement-commercial-${it.immatriculation}`}>
                        <option value="">Commercial…</option>
                        {commerciaux.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                )}
                <Input className="h-8 w-full sm:w-64" placeholder="Motif (obligatoire)" value={motif} onChange={e => setMotif(e.target.value)} data-testid={`rattachement-motif-${it.immatriculation}`} />
                <Button size="sm" disabled={!action || motif.trim().length < 5 || busy || (action === 'changer_commercial' && !commercialId)} onClick={submit} data-testid={`rattachement-submit-${it.immatriculation}`}>Appliquer</Button>
            </div>
        </li>
    );
}

export function RattachementPanel() {
    const [d, load] = useAnomalies();
    if (!d?.count) return null;
    return (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2 text-[13px] text-amber-900" data-testid="rattachement-panel">
            <p className="text-sm font-semibold">Ventes non comptées : véhicule et commercial dans deux agences ({d.count})</p>
            <p>Seul ce véhicule est modifié. Aucune donnée n'est corrigée automatiquement, et une commission déjà payée bloque la correction.</p>
            <ul className="space-y-2">{d.items.map(it => <Row key={it.vehicle_id} it={it} onDone={load} />)}</ul>
        </div>
    );
}
